/**
 * Sandbox Group detail page component.
 * Shows the sandboxes within a specific group.
 *
 * @module pages/SandboxGroupDetailPage
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import {
  ArrowLeft,
  FolderOpen,
  Box,
  Trash2,
  Plus,
  Plug,
  Pencil,
  Check,
  X,
  ChevronDown,
  Github,
  Globe,
  HardDrive,
  Play,
  Camera,
  KeyRound,
  Database,
  Package,
} from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  LoadingPage,
  Badge,
  ConfirmModal,
  getSandboxStateBadgeVariant,
  getDiskImageStateBadgeVariant,
  GeoRegionSelector,
  GEO_REGIONS,
  getSelectedGeoRegions,
  getRegionsFromGeoSelection,
  RegionMap,
} from '@/components/ui';
import type { GeoRegionKey } from '@/components/ui';
import {
  useSandboxGroup,
  useSandboxGroupSandboxes,
  useSandboxGroupDiskImages,
  useSandboxGroupSnapshots,
  useSandboxGroupSnapshotsCount,
  useSandboxGroupSecrets,
  useDeleteSandboxGroupSecret,
  useDeleteSandboxGroup,
  useDeleteSandbox,
  useResumeSandbox,
  useDeleteSandboxGroupDiskImage,
  useDeleteSandboxGroupSnapshot,
  useSandboxGroupVolumes,
  useDeleteSandboxGroupVolume,
  useUpdateSandboxGroup,
  useContentPackages,
  useDeleteContentPackage,
} from '@/hooks';
import { createGroupScopedAdapter } from '@/services/connectionServiceAdapter';
import { ConnectorsPage } from './ConnectorsPage';
import { formatDistanceToNow } from 'date-fns';
import type { SandboxResponse, SecretResponse } from '@/types';

/**
 * Sandbox Group detail page
 */
export function SandboxGroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<
    | 'sandboxes'
    | 'diskImages'
    | 'snapshots'
    | 'secrets'
    | 'volumes'
    | 'connections'
    | 'contentPackages'
  >('sandboxes');

  const { data: group, isLoading: isGroupLoading } = useSandboxGroup(id!);
  const { data: sandboxes, isLoading: isSandboxesLoading } = useSandboxGroupSandboxes(id!);
  const { data: privateDiskImages, isLoading: privateDiskImagesLoading } =
    useSandboxGroupDiskImages(id!);
  const { data: allSnapshots, isLoading: snapshotsLoading } = useSandboxGroupSnapshots(
    id!,
    activeTab === 'snapshots'
  );
  const { data: snapshotsCount } = useSandboxGroupSnapshotsCount(id!);
  const { data: groupSecrets, isLoading: secretsLoading } = useSandboxGroupSecrets(
    id!,
    activeTab === 'secrets'
  );
  const { data: groupVolumes, isLoading: groupVolumesLoading } = useSandboxGroupVolumes(
    id!,
    activeTab === 'volumes'
  );
  const { data: groupContentPackages, isLoading: groupContentPackagesLoading } = useContentPackages(
    id,
    activeTab === 'contentPackages'
  );
  const deleteGroupMutation = useDeleteSandboxGroup();
  const deleteSandboxMutation = useDeleteSandbox();
  const resumeSandboxMutation = useResumeSandbox();
  const deleteDiskImageMutation = useDeleteSandboxGroupDiskImage();
  const deleteSnapshotMutation = useDeleteSandboxGroupSnapshot();
  const deleteSecretMutation = useDeleteSandboxGroupSecret(id!);
  const deleteVolumeMutation = useDeleteSandboxGroupVolume();
  const deleteContentPackageMutation = useDeleteContentPackage();

  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [sandboxToDelete, setSandboxToDelete] = useState<SandboxResponse | null>(null);
  const [diskImageToDelete, setDiskImageToDelete] = useState<string | null>(null);
  const [snapshotToDelete, setSnapshotToDelete] = useState<string | null>(null);
  const [secretToDelete, setSecretToDelete] = useState<SecretResponse | null>(null);
  const [volumeToDelete, setVolumeToDelete] = useState<string | null>(null);
  const [contentPackageToDelete, setContentPackageToDelete] = useState<string | null>(null);
  const [showAddSandboxMenu, setShowAddSandboxMenu] = useState(false);
  const [isEditingRegions, setIsEditingRegions] = useState(false);
  const [selectedGeoRegions, setSelectedGeoRegions] = useState<Set<GeoRegionKey>>(new Set());
  const addSandboxMenuRef = useRef<HTMLDivElement>(null);
  const updateGroupMutation = useUpdateSandboxGroup();

  const groupAdapter = useMemo(() => createGroupScopedAdapter(id!), [id]);

  // Close add sandbox dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addSandboxMenuRef.current && !addSandboxMenuRef.current.contains(event.target as Node)) {
        setShowAddSandboxMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDeleteGroup = () => {
    if (id) {
      deleteGroupMutation.mutate(id, {
        onSuccess: () => navigate('/sandboxes'),
      });
    }
  };

  const handleDeleteSandbox = () => {
    if (sandboxToDelete) {
      deleteSandboxMutation.mutate(
        {
          id: sandboxToDelete.id,
          sandboxGroupId: sandboxToDelete.sandboxGroupId ?? id ?? undefined,
        },
        {
          onSuccess: () => setSandboxToDelete(null),
        }
      );
    }
  };

  const handleResumeSandbox = (sandboxId: string) => {
    resumeSandboxMutation.mutate({ id: sandboxId, sandboxGroupId: id });
  };

  const handleDeleteDiskImage = () => {
    if (diskImageToDelete && id) {
      deleteDiskImageMutation.mutate(
        { groupId: id, diskImageId: diskImageToDelete },
        { onSuccess: () => setDiskImageToDelete(null) }
      );
    }
  };

  const handleDeleteContentPackage = () => {
    if (contentPackageToDelete && id) {
      deleteContentPackageMutation.mutate(
        { id: contentPackageToDelete, sandboxGroupId: id },
        { onSuccess: () => setContentPackageToDelete(null) }
      );
    }
  };

  const handleDeleteSnapshot = () => {
    if (snapshotToDelete && id) {
      deleteSnapshotMutation.mutate(
        { groupId: id, snapshotId: snapshotToDelete },
        { onSuccess: () => setSnapshotToDelete(null) }
      );
    }
  };

  const handleDeleteSecret = () => {
    if (secretToDelete && id) {
      deleteSecretMutation.mutate(secretToDelete.id, {
        onSuccess: () => setSecretToDelete(null),
      });
    }
  };

  const handleDeleteVolume = () => {
    if (volumeToDelete && id) {
      deleteVolumeMutation.mutate(
        { groupId: id, volumeName: volumeToDelete },
        { onSuccess: () => setVolumeToDelete(null) }
      );
    }
  };

  // Region editing handlers
  const handleStartEditRegions = () => {
    const currentGeoRegions = getSelectedGeoRegions(group?.allowedLocations ?? []);
    setSelectedGeoRegions(currentGeoRegions);
    setIsEditingRegions(true);
  };

  const handleCancelEditRegions = () => {
    setIsEditingRegions(false);
    setSelectedGeoRegions(new Set());
  };

  const handleSaveRegions = () => {
    if (!id || !group) return;
    const allowedLocations = getRegionsFromGeoSelection(selectedGeoRegions);
    updateGroupMutation.mutate(
      {
        id,
        request: {
          labels: group.labels ?? {},
          connections: group.connections,
          allowedLocations: allowedLocations.length > 0 ? allowedLocations : undefined,
        },
      },
      {
        onSuccess: () => {
          setIsEditingRegions(false);
        },
      }
    );
  };

  // Get display-friendly names for allowed regions
  const allowedRegionsDisplay = useMemo(() => {
    const locations = group?.allowedLocations ?? [];
    if (locations.length === 0) return null;

    const geoRegions = getSelectedGeoRegions(locations);
    return Array.from(geoRegions)
      .map((key) => GEO_REGIONS[key].name)
      .join(', ');
  }, [group?.allowedLocations]);

  // Compute sandbox counts per region from actual sandbox data
  const regionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    sandboxes?.forEach((sandbox) => {
      if (sandbox.region) {
        counts[sandbox.region] = (counts[sandbox.region] || 0) + 1;
      }
    });
    return counts;
  }, [sandboxes]);

  if (isGroupLoading) {
    return <LoadingPage />;
  }

  if (!group) {
    return (
      <div>
        <PageHeader
          title="Group Not Found"
          breadcrumbs={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Sandboxes', href: '/sandboxes' },
            { label: 'Not Found' },
          ]}
        />
        <Card>
          <CardBody>
            <p className="text-gray-500">The requested sandbox group could not be found.</p>
            <Link to="/sandboxes">
              <Button
                variant="secondary"
                className="mt-4"
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Back to Groups
              </Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <FolderOpen className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            {group.id}
          </span>
        }
        description={`Created ${group.createdAt ? new Date(group.createdAt).toLocaleDateString() : 'Unknown'}`}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Sandboxes', href: '/sandboxes' },
          { label: group.id },
        ]}
        action={
          <div className="flex flex-wrap gap-2">
            <Link to="/sandboxes">
              <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
                Back
              </Button>
            </Link>
            {/* Add Sandbox dropdown */}
            <div className="relative" ref={addSandboxMenuRef}>
              <Button
                leftIcon={<Plus className="h-4 w-4" />}
                rightIcon={<ChevronDown className="h-4 w-4" />}
                onClick={() => setShowAddSandboxMenu(!showAddSandboxMenu)}
              >
                Add Sandbox
              </Button>
              {showAddSandboxMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-lg z-50">
                  <Link
                    to={`/sandboxes/new?groupId=${encodeURIComponent(group.id)}`}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-t-lg"
                    onClick={() => setShowAddSandboxMenu(false)}
                  >
                    <Plus className="h-4 w-4" />
                    <div>
                      <div className="font-medium">Standard Sandbox</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Full configuration options
                      </div>
                    </div>
                  </Link>
                  <Link
                    to={`/sandboxes/new/copilot?groupId=${encodeURIComponent(group.id)}`}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-b-lg border-t border-gray-200 dark:border-[#2a2a2a]"
                    onClick={() => setShowAddSandboxMenu(false)}
                  >
                    <Github className="h-4 w-4" />
                    <div>
                      <div className="font-medium">GitHub Copilot Sandbox</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Pre-configured for Copilot
                      </div>
                    </div>
                  </Link>
                </div>
              )}
            </div>
            <Button
              variant="danger"
              leftIcon={<Trash2 className="h-4 w-4" />}
              onClick={() => setShowDeleteGroupModal(true)}
            >
              Delete Group
            </Button>
          </div>
        }
      />

      {/* Labels */}
      {group.labels && Object.keys(group.labels).length > 0 && (
        <Card className="mb-6">
          <CardHeader title="Labels" />
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {Object.entries(group.labels).map(([key, value]) => (
                <Badge key={key} variant="neutral">
                  {key}: {value}
                </Badge>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Allowed Regions */}
      <Card className="mb-6">
        <CardHeader
          title="Allowed Regions"
          description="Geographic regions where sandboxes can be created"
          action={
            isEditingRegions ? (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<X className="h-4 w-4" />}
                  onClick={handleCancelEditRegions}
                  disabled={updateGroupMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  leftIcon={<Check className="h-4 w-4" />}
                  onClick={handleSaveRegions}
                  isLoading={updateGroupMutation.isPending}
                >
                  Save
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Pencil className="h-4 w-4" />}
                onClick={handleStartEditRegions}
              >
                Edit
              </Button>
            )
          }
        />
        <CardBody>
          {isEditingRegions ? (
            <GeoRegionSelector
              value={selectedGeoRegions}
              onChange={setSelectedGeoRegions}
              helperText="Select geographic regions to restrict sandbox placement"
            />
          ) : allowedRegionsDisplay ? (
            <div className="flex items-center gap-3">
              <Globe className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {allowedRegionsDisplay}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {group.allowedLocations?.length} region
                  {(group.allowedLocations?.length ?? 0) !== 1 ? 's' : ''} enabled
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Globe className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">All regions allowed</p>
                <p className="text-xs text-gray-400">
                  Sandboxes can be created in any available region
                </p>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Region Distribution Map - show when there are sandboxes */}
      {sandboxes && sandboxes.length > 0 && (
        <Card className="mb-6">
          <CardHeader
            title={`Sandbox Distribution (${Object.values(regionCounts).reduce((a, b) => a + b, 0)} running)`}
            description="Geographic distribution of running sandboxes in this group"
          />
          <CardBody>
            <RegionMap regionCounts={regionCounts} />
          </CardBody>
        </Card>
      )}

      {/* Tabbed section: Sandboxes / Disk Images / Snapshots / Connections */}
      <Card>
        {/* Tab bar */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('sandboxes')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'sandboxes'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <Box className="h-4 w-4" />
                Sandboxes ({sandboxes?.length ?? 0})
              </span>
            </button>
            <button
              onClick={() => setActiveTab('diskImages')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'diskImages'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <HardDrive className="h-4 w-4" />
                Disk Images
                {privateDiskImages !== undefined &&
                  ` (${privateDiskImages.filter((i) => i.status?.state === 'Ready').length})`}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('snapshots')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'snapshots'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <Camera className="h-4 w-4" />
                Snapshots{snapshotsCount !== undefined ? ` (${snapshotsCount})` : ''}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('secrets')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'secrets'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <KeyRound className="h-4 w-4" />
                Secrets{groupSecrets !== undefined ? ` (${groupSecrets.length})` : ''}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('volumes')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'volumes'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                Volumes{groupVolumes !== undefined ? ` (${groupVolumes.length})` : ''}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('contentPackages')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'contentPackages'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Content Packages
                {groupContentPackages !== undefined ? ` (${groupContentPackages.length})` : ''}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('connections')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'connections'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <Plug className="h-4 w-4" />
                Connections
              </span>
            </button>
          </nav>
        </div>

        <CardBody>
          {/* Sandboxes tab */}
          {activeTab === 'sandboxes' && (
            <div>
              <div className="flex justify-end mb-4">
                <div className="flex flex-wrap gap-2">
                  <Link to={`/sandboxes/new?groupId=${encodeURIComponent(group.id)}`}>
                    <Button variant="secondary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
                      Add Standard
                    </Button>
                  </Link>
                  <Link to={`/sandboxes/new/copilot?groupId=${encodeURIComponent(group.id)}`}>
                    <Button variant="secondary" size="sm" leftIcon={<Github className="h-4 w-4" />}>
                      Add Copilot
                    </Button>
                  </Link>
                </div>
              </div>
              {isSandboxesLoading ? (
                <p className="text-sm text-gray-500">Loading sandboxes...</p>
              ) : sandboxes && sandboxes.length > 0 ? (
                <div className="space-y-3">
                  {sandboxes.map((sandbox) => (
                    <div
                      key={sandbox.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Box className="h-5 w-5 text-gray-400" />
                        <div>
                          <Link
                            to={`/sandbox-groups/${encodeURIComponent(id!)}/sandboxes/${encodeURIComponent(sandbox.id)}`}
                            className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            {sandbox.id}
                          </Link>
                          {sandbox.labels && Object.keys(sandbox.labels).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {Object.entries(sandbox.labels)
                                .slice(0, 2)
                                .map(([key, value]) => (
                                  <span
                                    key={key}
                                    className="text-xs text-gray-500 dark:text-gray-400"
                                  >
                                    {key}: {value}
                                  </span>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {sandbox.state && (
                          <Badge variant={getSandboxStateBadgeVariant(sandbox.state)}>
                            {sandbox.state}
                          </Badge>
                        )}
                        {(sandbox.state === 'Stopped' || sandbox.state === 'Idle') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResumeSandbox(sandbox.id)}
                            className="text-success-600 hover:text-success-700 dark:text-success-400"
                            isLoading={
                              resumeSandboxMutation.isPending &&
                              resumeSandboxMutation.variables?.id === sandbox.id
                            }
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSandboxToDelete(sandbox)}
                          className="text-danger-600 hover:text-danger-700 dark:text-danger-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Box className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    No sandboxes in this group yet.
                  </p>
                  <div className="flex justify-center gap-2 mt-3">
                    <Link to={`/sandboxes/new?groupId=${encodeURIComponent(group.id)}`}>
                      <Button variant="secondary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
                        Add Standard
                      </Button>
                    </Link>
                    <Link to={`/sandboxes/new/copilot?groupId=${encodeURIComponent(group.id)}`}>
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<Github className="h-4 w-4" />}
                      >
                        Add Copilot
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Disk Images tab */}
          {activeTab === 'diskImages' && (
            <div>
              {privateDiskImagesLoading ? (
                <p className="text-sm text-gray-500">Loading disk images...</p>
              ) : privateDiskImages &&
                privateDiskImages.filter((i) => i.status?.state === 'Ready').length > 0 ? (
                <div className="space-y-2">
                  {privateDiskImages
                    .filter((img) => img.status?.state === 'Ready')
                    .map((img) => {
                      const displayName = img.name || (img.labels && img.labels['name']) || img.id;
                      return (
                        <div
                          key={img.id}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <HardDrive className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {displayName !== img.id
                                  ? `${displayName} (${img.id.substring(0, 8)}…)`
                                  : img.id}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {img.image?.base || 'Built from Dockerfile'}
                                </span>
                                {img.status?.createdAt && (
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    ·{' '}
                                    {formatDistanceToNow(new Date(img.status.createdAt), {
                                      addSuffix: true,
                                    })}
                                  </span>
                                )}
                              </div>
                              {img.labels && Object.keys(img.labels).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {Object.entries(img.labels as Record<string, string>).map(
                                    ([key, value]) => (
                                      <span
                                        key={key}
                                        className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                                      >
                                        {key}: {value}
                                      </span>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={getDiskImageStateBadgeVariant(
                                img.status?.state || 'Unknown'
                              )}
                            >
                              {img.status?.state || 'Unknown'}
                            </Badge>
                            <Link
                              to={`/sandboxes/new?groupId=${encodeURIComponent(group.id)}&diskImageId=${encodeURIComponent(img.id)}`}
                            >
                              <Button
                                variant="secondary"
                                size="sm"
                                leftIcon={<Play className="h-3.5 w-3.5" />}
                              >
                                Create Sandbox
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDiskImageToDelete(img.id)}
                              className="text-danger-600 hover:text-danger-700 dark:text-danger-400"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <HardDrive className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    No ready disk images available.
                  </p>
                  <Link to="/disk-images">
                    <Button variant="secondary" size="sm" className="mt-2">
                      Go to Disk Images
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Snapshots tab */}
          {activeTab === 'snapshots' && (
            <div>
              {snapshotsLoading ? (
                <p className="text-sm text-gray-500">Loading snapshots...</p>
              ) : allSnapshots && allSnapshots.length > 0 ? (
                <div className="space-y-2">
                  {allSnapshots.map((snapshot) => (
                    <div
                      key={snapshot.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Camera className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {snapshot.id}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {snapshot.sandboxId && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                from {snapshot.sandboxId}
                              </span>
                            )}
                            {snapshot.resources && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                · {snapshot.resources.cpu} CPU, {snapshot.resources.memory} RAM
                                {snapshot.resources.disk ? `, ${snapshot.resources.disk} disk` : ''}
                              </span>
                            )}
                            {snapshot.createdAtUtc && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                ·{' '}
                                {formatDistanceToNow(new Date(snapshot.createdAtUtc), {
                                  addSuffix: true,
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/sandboxes/new?groupId=${encodeURIComponent(group.id)}&snapshotId=${encodeURIComponent(snapshot.id)}`}
                        >
                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<Play className="h-3.5 w-3.5" />}
                          >
                            Create Sandbox
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSnapshotToDelete(snapshot.id)}
                          className="text-danger-600 hover:text-danger-700 dark:text-danger-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Camera className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    No snapshots available.
                  </p>
                  <Link to="/snapshots">
                    <Button variant="secondary" size="sm" className="mt-2">
                      Go to Snapshots
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Secrets tab */}
          {activeTab === 'secrets' && (
            <div>
              <div className="flex justify-end mb-4">
                <Link to={`/sandbox-groups/${encodeURIComponent(group.id)}/secrets`}>
                  <Button variant="secondary" size="sm" leftIcon={<KeyRound className="h-4 w-4" />}>
                    Manage Secrets
                  </Button>
                </Link>
              </div>
              {secretsLoading ? (
                <p className="text-sm text-gray-500">Loading secrets...</p>
              ) : groupSecrets && groupSecrets.length > 0 ? (
                <div className="space-y-2">
                  {groupSecrets.map((secret) => (
                    <div
                      key={secret.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <KeyRound className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {secret.id}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {secret.createdAt && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Created{' '}
                                {formatDistanceToNow(new Date(secret.createdAt), {
                                  addSuffix: true,
                                })}
                              </span>
                            )}
                            {secret.updatedAt && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                · Updated{' '}
                                {formatDistanceToNow(new Date(secret.updatedAt), {
                                  addSuffix: true,
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSecretToDelete(secret)}
                        className="text-danger-600 hover:text-danger-700 dark:text-danger-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <KeyRound className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    No secrets available.
                  </p>
                  <Link to={`/sandbox-groups/${encodeURIComponent(group.id)}/secrets`}>
                    <Button variant="secondary" size="sm" className="mt-2">
                      Add Secrets
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Volumes tab */}
          {activeTab === 'volumes' && (
            <div>
              {groupVolumesLoading ? (
                <p className="text-sm text-gray-500">Loading volumes...</p>
              ) : groupVolumes && groupVolumes.length > 0 ? (
                <div className="space-y-2">
                  {groupVolumes.map((vol) => (
                    <div
                      key={vol.volumeName}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Database className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {vol.volumeName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Type: {vol.type}
                            {vol.labels && Object.keys(vol.labels).length > 0 && (
                              <>
                                {' '}
                                · Labels:{' '}
                                {Object.entries(vol.labels)
                                  .map(([k, v]) => `${k}=${v}`)
                                  .join(', ')}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="success">Active</Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setVolumeToDelete(vol.volumeName)}
                          className="text-danger-600 hover:text-danger-700 dark:text-danger-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Database className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    No volumes in this group.
                  </p>
                  <Link to={`/sandbox-groups/${encodeURIComponent(id!)}/volumes/new`}>
                    <Button variant="secondary" size="sm" className="mt-2">
                      Create Volume
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Connections tab */}
          {activeTab === 'connections' && <ConnectorsPage serviceAdapter={groupAdapter} embedded />}

          {activeTab === 'contentPackages' && (
            <div>
              <div className="flex justify-end mb-4">
                <Link to={`/sandbox-groups/${encodeURIComponent(group.id)}/content-packages`}>
                  <Button variant="secondary" size="sm" leftIcon={<Package className="h-4 w-4" />}>
                    Manage Content Packages
                  </Button>
                </Link>
              </div>
              {groupContentPackagesLoading ? (
                <p className="text-sm text-gray-500">Loading content packages...</p>
              ) : groupContentPackages && groupContentPackages.length > 0 ? (
                <div className="space-y-2">
                  {groupContentPackages.map((cp) => (
                    <div
                      key={cp.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Package className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {cp.id}
                          </p>
                          {cp.createdOn && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Created{' '}
                              {formatDistanceToNow(new Date(cp.createdOn), { addSuffix: true })}
                            </span>
                          )}
                          {cp.labels && Object.keys(cp.labels).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {Object.entries(cp.labels as Record<string, string>).map(
                                ([key, value]) => (
                                  <span
                                    key={key}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                                  >
                                    {key}: {value}
                                  </span>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setContentPackageToDelete(cp.id)}
                        className="text-danger-600 hover:text-danger-700 dark:text-danger-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    No content packages in this group.
                  </p>
                  <Link to={`/sandbox-groups/${encodeURIComponent(group.id)}/content-packages`}>
                    <Button variant="secondary" size="sm" className="mt-2">
                      Upload Content Package
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Delete Group Confirmation */}
      <ConfirmModal
        isOpen={showDeleteGroupModal}
        onClose={() => setShowDeleteGroupModal(false)}
        onConfirm={handleDeleteGroup}
        title="Delete Sandbox Group"
        message={`Are you sure you want to delete group "${group.id}"? Sandboxes in this group will not be deleted but will no longer be associated with a group.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteGroupMutation.isPending}
      />

      {/* Delete Sandbox Confirmation */}
      <ConfirmModal
        isOpen={!!sandboxToDelete}
        onClose={() => setSandboxToDelete(null)}
        onConfirm={handleDeleteSandbox}
        title="Delete Sandbox"
        message={`Are you sure you want to delete sandbox "${sandboxToDelete?.id}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteSandboxMutation.isPending}
      />

      {/* Delete Disk Image Confirmation */}
      <ConfirmModal
        isOpen={!!diskImageToDelete}
        onClose={() => setDiskImageToDelete(null)}
        onConfirm={handleDeleteDiskImage}
        title="Delete Disk Image"
        message={`Are you sure you want to delete disk image "${diskImageToDelete}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteDiskImageMutation.isPending}
      />

      {/* Delete Content Package Confirmation */}
      <ConfirmModal
        isOpen={!!contentPackageToDelete}
        onClose={() => setContentPackageToDelete(null)}
        onConfirm={handleDeleteContentPackage}
        title="Delete Content Package"
        message={`Are you sure you want to delete content package "${contentPackageToDelete}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteContentPackageMutation.isPending}
      />

      {/* Delete Snapshot Confirmation */}
      <ConfirmModal
        isOpen={!!snapshotToDelete}
        onClose={() => setSnapshotToDelete(null)}
        onConfirm={handleDeleteSnapshot}
        title="Delete Snapshot"
        message={`Are you sure you want to delete snapshot "${snapshotToDelete}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteSnapshotMutation.isPending}
      />

      {/* Delete Secret Confirmation */}
      <ConfirmModal
        isOpen={!!secretToDelete}
        onClose={() => setSecretToDelete(null)}
        onConfirm={handleDeleteSecret}
        title="Delete Secret"
        message={`Are you sure you want to delete secret "${secretToDelete?.id}"? This will permanently remove all its key-value pairs.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteSecretMutation.isPending}
      />

      {/* Delete Volume Confirmation */}
      <ConfirmModal
        isOpen={!!volumeToDelete}
        onClose={() => setVolumeToDelete(null)}
        onConfirm={handleDeleteVolume}
        title="Delete Volume"
        message={`Are you sure you want to delete volume "${volumeToDelete}"? This will permanently delete the volume and all its contents.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteVolumeMutation.isPending}
      />
    </div>
  );
}
